import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Cell,
} from 'recharts';
import {
  ArrowLeft, Sparkles, RefreshCw, Camera, Download, FileSpreadsheet, FileText,
  Star, AlertTriangle, DollarSign, TrendingUp, Award, Users, ArrowRight, ListChecks, Clock, CheckCircle2, Activity, Lock,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';

const QUADRANT_COLORS = {
  star: 'hsl(var(--success))',
  fairly_paid: 'hsl(var(--primary))',
  steady: 'hsl(var(--muted-foreground))',
  at_risk: 'hsl(var(--destructive))',
};
const QUADRANT_LABELS = {
  star: 'Star', fairly_paid: 'Fairly paid', steady: 'Steady', at_risk: 'At risk',
};

const fmtMoney = (n, ccy = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function AdminIntelligenceDepartment() {
  const { t, i18n } = useTranslation();
  const { user: authUser } = useApp();
  const isConsultant = authUser?.role === 'hm_advisor';
  const navigate = useNavigate();
  const { deptId } = useParams();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [overview, setOverview] = useState(null);
  const [actions, setActions] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Prompt 8: drilldown for clicked collaborator (reuses Intelligence dialog UX)
  const [drilldown, setDrilldown] = useState(null);
  const [collabStatus, setCollabStatus] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);

  // Find which company this dept belongs to (need to look up company → dept list)
  useEffect(() => {
    (async () => {
      try {
        const cos = await adminApi.listCompanies();
        setCompanies(cos);
        for (const co of cos) {
          const depts = await adminApi.listDepartments(co.id, true);
          if (depts.find(d => d.id === deptId)) {
            setCompanyId(co.id);
            break;
          }
        }
      } catch { toast.error(t('toasts.failedToResolveDepartment')); }
    })();
  }, [deptId]);

  useEffect(() => {
    if (!companyId || !deptId) return;
    (async () => {
      try {
        const [ov, ac, sn] = await Promise.all([
          adminApi.intelligenceOverview(companyId, deptId),
          adminApi.listActions(companyId, { department_id: deptId }),
          adminApi.listSnapshots(companyId, deptId),
        ]);
        setOverview(ov);
        setActions(ac);
        setSnapshots(sn);
      } catch (e) {
        toast.error(t('toasts.failedToLoadDepartment'));
      }
    })();
  }, [companyId, deptId]);

  const dept = overview?.department_rollup?.find(d => d.department?.id === deptId)?.department
    || overview?.employees?.[0]?.department
    || { name: '…' };
  const kpis = overview?.kpis;
  const employees = overview?.employees || [];
  const ccy = kpis?.currency || 'USD';
  const medianCost = kpis?.median_cost || 0;

  const scatterData = useMemo(() => employees.map(e => ({
    name: e.user.name, value: e.value.value_score, cost: e.cost.loaded, quadrant: e.quadrant,
  })), [employees]);

  const trendData = useMemo(() => snapshots.map(s => ({
    date: new Date(s.snapshot_date).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
    avg_value: s.avg_value,
    positive_roi: s.positive_roi_pct,
    at_risk: s.at_risk_count,
  })), [snapshots, i18n.language]);

  const generateNarrative = async () => {
    if (!companyId || !deptId) return;
    setNarrativeLoading(true);
    try {
      const r = await adminApi.intelligenceNarrative(companyId, deptId);
      setNarrative(r);
    } catch { toast.error(t('toasts.failedToGenerateNarrative')); }
    finally { setNarrativeLoading(false); }
  };

  // Prompt 8: load collaborator status when drilldown opens
  useEffect(() => {
    if (!drilldown) { setCollabStatus(null); return; }
    (async () => {
      try {
        const s = await adminApi.getCollabStatus(drilldown.user.id);
        setCollabStatus(s);
      } catch { setCollabStatus({ status: 'on_track', history: [] }); }
    })();
  }, [drilldown]);

  const STATUS_LABEL = useMemo(() => ({
    on_track:    { en: 'On Track',    es: 'En curso' },
    take_action: { en: 'Take Action', es: 'Tomar Acción' },
    dismissed:   { en: 'Dismissed',   es: 'Descartada' },
  }), []);
  const localeKey = i18n.language?.startsWith('es') ? 'es' : 'en';
  const statusText = (k) => STATUS_LABEL[k]?.[localeKey] || k;

  const confirmStatusChange = async () => {
    if (!drilldown || !pendingStatus) return;
    setStatusSaving(true);
    try {
      const updated = await adminApi.updateCollabStatus(drilldown.user.id, pendingStatus);
      setCollabStatus(updated);
      toast.success(t('intelligence.statusUpdated', { defaultValue: 'Status updated' }));
      setPendingStatus(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    } finally { setStatusSaving(false); }
  };

  const captureSnapshot = async () => {
    if (!companyId || !deptId) return;
    setSnapshotLoading(true);
    try {
      const s = await adminApi.createSnapshot(companyId, deptId, `${dept?.name} drilldown`);
      setSnapshots(prev => [...prev, s]);
      toast.success(t('intelligence.snapshotCaptured', { defaultValue: 'Snapshot captured' }));
    } catch { toast.error(t('toasts.snapshotFailed')); }
    finally { setSnapshotLoading(false); }
  };

  const today = new Date();
  const isOverdue = (a) => a.due_date && new Date(a.due_date) < today && !['done','dismissed'].includes(a.status);
  const counts = useMemo(() => actions.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status]||0)+1 }), {}), [actions]);

  return (
    <>
      <Topbar
        title={dept?.name || '—'}
        subtitle={t('intelligence.deptDrilldown', { defaultValue: 'Department-scoped intelligence & decisions' })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/intelligence" data-testid="intel-back"><ArrowLeft className="h-4 w-4 mr-1" />{t('intelligence.backToIntelligence', { defaultValue: 'Back to TalentIntelligence' })}</Link>
            </Button>
            <Button variant="outline" size="sm" onClick={captureSnapshot} disabled={snapshotLoading} data-testid="dept-snapshot-btn"><Camera className="h-4 w-4 mr-1.5" />{t('common.snapshot')}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="dept-export-btn"><Download className="h-4 w-4 mr-1.5" />{t('common.export')}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild><a href={adminApi.exportIntelCsvUrl(companyId, deptId)} target="_blank" rel="noreferrer"><FileSpreadsheet className="h-4 w-4 mr-2" />CSV</a></DropdownMenuItem>
                <DropdownMenuItem asChild><a href={adminApi.exportIntelPdfUrl(companyId, deptId, false)} target="_blank" rel="noreferrer"><FileText className="h-4 w-4 mr-2" />PDF</a></DropdownMenuItem>
                <DropdownMenuItem asChild><a href={adminApi.exportIntelPdfUrl(companyId, deptId, true)} target="_blank" rel="noreferrer"><FileText className="h-4 w-4 mr-2" />{t('intelligence.pdfWithAi')}</a></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="default" onClick={generateNarrative} disabled={narrativeLoading} data-testid="dept-narrative-btn">
              {narrativeLoading ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5 text-accent" />}
              {t('intelligence.generateNarrative', { defaultValue: 'AI summary' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="dept-dashboard">
        {/* Hero */}
        <Card className="p-6 md:p-7 shadow-card bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 dot-pattern opacity-[0.08]" />
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-accent/30 rounded-full blur-3xl" />
          <div className="relative flex items-start gap-6">
            <div className="relative h-28 w-28 shrink-0">
              <svg className="w-full h-full -rotate-90">
                <circle cx="50%" cy="50%" r="48" stroke="rgba(255,255,255,0.15)" strokeWidth="9" fill="none" />
                <circle cx="50%" cy="50%" r="48" stroke="white" strokeWidth="9" fill="none" strokeDasharray={`${(kpis?.avg_value || 0) * 3.01} 999`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display text-3xl font-bold tabular-nums" data-testid="dept-avg-value">{kpis?.avg_value ?? '—'}</div>
                <div className="text-xs uppercase tracking-wider font-semibold text-white/70 mt-0.5">avg value</div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <Badge className="bg-white/10 border-white/20 text-white">{dept?.code || 'Department'}</Badge>
              {narrative ? (
                <p className="text-sm md:text-[15px] text-white/90 mt-3 leading-relaxed whitespace-pre-line" data-testid="dept-narrative-text">{narrative.narrative}</p>
              ) : (
                <>
                  <h2 className="font-display text-2xl md:text-3xl font-bold mt-3 tracking-tight">{dept?.name}</h2>
                  <p className="text-sm text-white/75 mt-3">
                    {kpis?.employees ?? 0} {t('intelligence.employees', { defaultValue: 'employees' })} · {kpis?.star_count ?? 0} stars · {kpis?.at_risk_count ?? 0} at risk · {fmtMoney(kpis?.total_cost, ccy)}
                  </p>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* KPI strip */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <Mini label={t('intelligence.avgValue', { defaultValue: 'Avg value' })} value={kpis ? `${kpis.avg_value}/100` : '—'} icon={TrendingUp} />
          <Mini label={t('intelligence.totalCost', { defaultValue: 'Total cost' })} value={kpis ? fmtMoney(kpis.total_cost, ccy) : '—'} icon={DollarSign} />
          <Mini label={t('intelligence.positiveRoi', { defaultValue: 'Positive ROI' })} value={kpis ? `${kpis.positive_roi_pct}%` : '—'} icon={Award} />
          <Mini label={t('common.stars')} value={kpis?.star_count ?? '—'} icon={Star} tone="success" />
          <Mini label={t('intelligence.atRiskLabel', { defaultValue: 'At-risk' })} value={kpis?.at_risk_count ?? '—'} icon={AlertTriangle} tone="destructive" />
        </div>

        {/* Scatter */}
        <Card className="p-6 shadow-card">
          <h3 className="font-display font-bold text-lg tracking-tight">{t('intelligence.valueVsCost', { defaultValue: 'Value vs cost' })}</h3>
          <p className="text-sm text-muted-foreground">{t('intelligence.deptScatter', { defaultValue: 'Each dot is one team member' })}</p>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" dataKey="cost" tickFormatter={(v)=>fmtMoney(v, ccy)} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                <YAxis type="number" dataKey="value" domain={[0, 100]} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                <Tooltip content={({ payload }) => {
                  if (!payload?.length) return null;
                  const d = payload[0].payload;
                  return <div className="bg-card border rounded-md p-3 shadow text-xs"><div className="font-semibold">{d.name}</div><div>Value: {d.value}/100</div><div>Cost: {fmtMoney(d.cost, ccy)}</div><div>{QUADRANT_LABELS[d.quadrant]}</div></div>;
                }} />
                <Scatter data={scatterData}>{scatterData.map((d, i) => <Cell key={i} fill={QUADRANT_COLORS[d.quadrant] || 'hsl(var(--muted-foreground))'} />)}</Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Trend (dept) */}
        {trendData.length > 1 && (
          <Card className="p-6 shadow-card">
            <h3 className="font-display font-bold text-lg tracking-tight">{t('intelligence.trend', { defaultValue: 'Trend over time' })}</h3>
            <p className="text-sm text-muted-foreground">{t('intelligence.deptTrendSub', { defaultValue: 'Department snapshots — values, ROI, and risk' })}</p>
            <div className="h-56 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ left: -10, right: 20, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="avg_value" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} name={t('intelligence.avgValue', { defaultValue: 'Avg value' })} />
                  <Line type="monotone" dataKey="positive_roi" stroke="hsl(var(--success))" strokeWidth={2} dot={{ r: 3 }} name={t('common.roiPct')} />
                  <Line type="monotone" dataKey="at_risk" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ r: 3 }} name={t('common.atRiskShort')} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Employees */}
          <Card className="p-6 shadow-card">
            <h3 className="font-display font-bold text-lg tracking-tight">{t('intelligence.employees', { defaultValue: 'Employees' })}</h3>
            <p className="text-sm text-muted-foreground">{t('intelligence.median', { defaultValue: 'median' })}: {fmtMoney(medianCost, ccy)}</p>
            <table className="w-full text-sm mt-4">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                  <th className="py-2 pr-3">{t('intelligence.employee', { defaultValue: 'Employee' })}</th>
                  <th className="py-2 px-3 text-right">{t('intelligence.value', { defaultValue: 'Value' })}</th>
                  <th className="py-2 px-3 text-right">{t('intelligence.cost', { defaultValue: 'Cost' })}</th>
                  <th className="py-2 pl-3">{t('intelligence.quadrant', { defaultValue: 'Quadrant' })}</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(e => (
                  <tr key={e.user.id}
                      className="border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                      onClick={() => setDrilldown(e)}
                      data-testid={`dept-emp-${e.user.id}`}>
                    <td className="py-3 pr-3">
                      <div className="font-medium">{e.user.name}</div>
                      <div className="text-xs text-muted-foreground">{e.user.position || '—'}</div>
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums"><span className="font-bold">{e.value.value_score}</span><span className="text-muted-foreground">/100</span></td>
                    <td className="py-3 px-3 text-right tabular-nums">{fmtMoney(e.cost.loaded, ccy)}</td>
                    <td className="py-3 pl-3"><Badge className="text-xs" style={e.quadrant ? { background: `${QUADRANT_COLORS[e.quadrant]}20`, color: QUADRANT_COLORS[e.quadrant] } : undefined} variant={e.quadrant ? undefined : 'secondary'}>{QUADRANT_LABELS[e.quadrant] || t('intelligence.unevaluated', { defaultValue: 'Sin evaluar' })}</Badge></td>
                  </tr>
                ))}
                {!employees.length && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground italic">{t('intelligence.noEmployees', { defaultValue: 'No employees in this department.' })}</td></tr>}
              </tbody>
            </table>
          </Card>

          {/* Decisions for this dept */}
          <Card className="p-6 shadow-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">{t('intelligence.decisionsHere', { defaultValue: 'Decisions for this department' })}</h3>
                <p className="text-sm text-muted-foreground">
                  {(counts.pending||0)} {t('decisions.pending', { defaultValue: 'pending' })} · {(counts.in_progress||0)} {t('decisions.in_progress', { defaultValue: 'in progress' })} · {(counts.done||0)} {t('decisions.done', { defaultValue: 'done' })}
                </p>
              </div>
              <Button variant="ghost" size="sm" asChild><Link to="/admin/intelligence/decisions" data-testid="dept-go-decisions">{t('intelligence.allDecisions', { defaultValue: 'All' })}<ArrowRight className="h-3 w-3 ml-1" /></Link></Button>
            </div>
            {actions.length ? (
              <div className="space-y-2">
                {actions.slice(0, 8).map(a => (
                  <div key={a.id} className="p-3 rounded-lg border" data-testid={`dept-action-${a.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{a.suggestion_title}</p>
                        <p className="text-xs text-muted-foreground">{a.user?.name} · {a.suggestion_type?.replace('_', ' ')}</p>
                        {(a.assignee_name || a.due_date) && (
                          <p className="text-xs mt-1.5 flex flex-wrap gap-2">
                            {a.assignee_name && <span><Users className="h-3 w-3 inline mr-1" />{a.assignee_name}</span>}
                            {a.due_date && <span className={isOverdue(a) ? 'text-destructive font-medium' : ''}><Clock className="h-3 w-3 inline mr-1" />{new Date(a.due_date).toLocaleDateString(i18n.language)}{isOverdue(a) && <span className="ml-1">⚠ overdue</span>}</span>}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs capitalize shrink-0">{a.status.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic py-6 text-center">{t('intelligence.noDeptActions', { defaultValue: 'No decisions logged yet.' })}</p>
            )}
          </Card>
        </div>
      </div>

      {/* Prompt 8: Collaborator drilldown (full detail view) */}
      <Dialog open={!!drilldown} onOpenChange={(o) => !o && setDrilldown(null)}>
        <DialogContent className="max-w-2xl" data-testid="dept-drilldown-dialog">
          {drilldown && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{drilldown.user.name}</DialogTitle>
                <DialogDescription>{drilldown.user.position || '—'} · {drilldown.department?.name || '—'}</DialogDescription>
              </DialogHeader>

              {/* Prompt 7: Editable collaborator status (hm_advisor only — Prompt 9 edit gating) */}
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30" data-testid="dept-collab-status">
                <Activity className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('intelligence.collabStatus', { defaultValue: 'Collaborator status' })}</p>
                  {isConsultant ? (
                    <Select
                      value={collabStatus?.status || 'on_track'}
                      onValueChange={(v) => { if (v !== (collabStatus?.status || 'on_track')) setPendingStatus(v); }}
                    >
                      <SelectTrigger className="mt-1 h-8 w-48" data-testid="dept-collab-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on_track">{statusText('on_track')}</SelectItem>
                        <SelectItem value="take_action">{statusText('take_action')}</SelectItem>
                        <SelectItem value="dismissed">{statusText('dismissed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium mt-1 flex items-center gap-1.5" data-testid="dept-collab-status-readonly">
                      {statusText(collabStatus?.status || 'on_track')}
                      <Lock className="h-3 w-3 text-muted-foreground" title={t('intelligence.editConsultantOnly', { defaultValue: 'Edit restricted to consultants' })} />
                    </p>
                  )}
                </div>
                {collabStatus?.updated_at && (
                  <div className="text-xs text-muted-foreground text-right shrink-0">
                    <div>{t('intelligence.lastChanged', { defaultValue: 'Last changed' })}</div>
                    <div className="tabular-nums">{new Date(collabStatus.updated_at).toLocaleDateString(i18n.language)}</div>
                    {collabStatus.updated_by_name && <div>{t('intelligence.by', { defaultValue: 'by' })} {collabStatus.updated_by_name}</div>}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <MiniField label={t('intelligence.value', { defaultValue: 'Value' })} value={`${drilldown.value.value_score}/100`} />
                <MiniField label={t('intelligence.cost', { defaultValue: 'Cost' })} value={fmtMoney(drilldown.cost.loaded, ccy)} sub={`${fmtMoney(drilldown.cost.monthly, ccy)} / ${i18n.language?.startsWith('es') ? 'mes' : 'mo'}`} />
                <MiniField label={t('intelligence.tenure', { defaultValue: 'Tenure' })} value={`${drilldown.tenure_years} ${i18n.language?.startsWith('es') ? 'años' : 'yrs'}`} />
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">{t('intelligence.valueBreakdown', { defaultValue: 'Value breakdown' })}</p>
                <div className="space-y-1.5">
                  {Object.entries(drilldown.value.breakdown).map(([k, b]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="capitalize">{k.replace('_', ' ')} <span className="text-muted-foreground">({b.weight_pct}%)</span></span>
                      <span className="tabular-nums">
                        {b.missing
                          ? <span className="text-destructive">— {t('intelligence.missing', { defaultValue: 'missing' })}</span>
                          : <><span className="font-bold">{b.raw}</span> · <span className="text-muted-foreground">+{b.weighted}</span></>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">{t('intelligence.suggestions', { defaultValue: 'Recommendations' })}</p>
                <div className="space-y-2">
                  {(drilldown.suggestions || []).map((s, i) => (
                    <div key={i} className="p-3 rounded-lg border text-sm">
                      <p className="font-semibold">{s.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.detail}</p>
                    </div>
                  ))}
                  {!(drilldown.suggestions || []).length && (
                    <p className="text-xs italic text-muted-foreground">{t('intelligence.noSuggestions', { defaultValue: 'No suggestions for this employee.' })}</p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDrilldown(null)} data-testid="dept-drilldown-back"><ArrowLeft className="h-4 w-4 mr-1" />{t('common.back', { defaultValue: 'Back' })}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Status change confirmation */}
      <Dialog open={!!pendingStatus} onOpenChange={(o) => !o && setPendingStatus(null)}>
        <DialogContent className="max-w-md" data-testid="dept-status-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('intelligence.statusConfirmTitle', { defaultValue: 'Confirm status change' })}</DialogTitle>
            <DialogDescription>
              {i18n.language?.startsWith('es')
                ? `¿Estás seguro de que deseas cambiar el estado a "${statusText(pendingStatus)}"?`
                : `Are you sure you want to change the status to "${statusText(pendingStatus)}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingStatus(null)} data-testid="dept-status-cancel">{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={confirmStatusChange} disabled={statusSaving} data-testid="dept-status-confirm">{statusSaving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.confirm', { defaultValue: 'Confirm' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MiniField({ label, value, sub }) {
  return (
    <div className="p-3 rounded-lg bg-muted/40">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="font-display text-lg font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Mini({ label, value, icon: Icon, tone = 'primary' }) {
  return (
    <Card className="p-5 shadow-card">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 text-${tone}`} />
      </div>
      <p className="font-display text-2xl font-bold tracking-tight tabular-nums mt-2">{value}</p>
    </Card>
  );
}
